import React, { useState, useEffect, useRef } from "react";
import { Search } from "react-feather";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingSpinner from "../../LoadingSpinner";
import {generatePerformanceFinanceReport, getReportHeader} from "../../../api/reportApi";
import { FORMS, MODULES } from "../../../const";
import { Grid } from "../job-report/ReportGrid";
import PerformanceFinanceSearch from "./PerformanceFinanceSearch";
import InfoList from "../../InfoList";
import * as formatter from "echarts";
import useUserCompany from "../../../hooks/useUserCompany";

function PerformanceFinanceScreen() {
    const auth = useSelector(
        (state) => state.user.permissions[MODULES.Transaction]
    );
    const permissions = auth?.[FORMS.MIS.REPORT.form];

    const [activeAccordion, setActiveAccordion] = useState(1);
    const [searchResults, setSearchResults] = useState([]);
    const [config, setConfig] = useState([]);
    const [isSearchClicked, setSearchClicked] = useState(false);
    const [isSearchResultsPending, setSearchResultsPending] = useState(false);
    const [isHeaderLoading, setIsHeaderLoading] = useState(true);
    const [dateType, setDateType] = useState(1);
    const [reportName, setReportName] = useState("Performance Finance Report");
    const [summaryData, setSummaryData] = useState(null);
    const gridRef = useRef();
    const userCompany = useUserCompany();
    const userCurrencyCode = userCompany ? userCompany.comCurrencyCode : null;

    const formatter = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: userCurrencyCode,
    });

    const mapJobReportsToGridData = (reports) => {
        return reports.map((report) => ({
            ...report?.basicInfo,
            ...report?.cargoInfo,
            ...report?.otherInfo,
            ...report?.statusInfo,
            ...report?.timingInfo,
            ...report?.financeInfo,
            ...report?.locationInfo,
            ...report?.shipmentInfo
        }));
    };

    // Fetch header config on mount
    useEffect(() => {
        const fetchHeaders = async () => {
            setIsHeaderLoading(true);
            try {
                const response = await getReportHeader("performance_finance");
                if (response?.reportDetails?.length > 0) {
                    const dynamicConfig = response.reportDetails.map((detail) => ({
                        field: detail.columnName,
                        headerName: detail.columnAlias,
                        type: detail.dataType,
                    }));
                    setConfig(dynamicConfig);
                } else {
                    throw new Error("No report headers available");
                }
            } catch (error) {
                toast.error("Error loading headers: " + error.message, {
                    autoClose: 5000,
                    containerId: "Main",
                });
            } finally {
                setIsHeaderLoading(false);
                setSearchResults([]);
                setSearchClicked(false);
            }
        };

        fetchHeaders();
    }, []);

    // Handle Search
    const handleSearch = async (searchParams) => {
        if (config.length === 0) {
            toast.warn("Report headers not loaded yet.", {
                autoClose: 3000,
                containerId: "Main",
            });
            return;
        }

        setSearchClicked(true);
        setSearchResultsPending(true);
        setSearchResults([]);

        try {
            const response = await generatePerformanceFinanceReport(searchParams);
            if (response?.reportData?.length > 0) {
                const mappedResults = mapJobReportsToGridData(response.reportData);
                console.log(mappedResults, "mapped results--")
                setSearchResults(mappedResults);
            } else {
                // throw new Error("No job reports found.");
            }

            if (response?.summery) {
                const summaryResponse = response.summery;
                const formattedSummary = [
                    {
                        title: "Completed Jobs",
                        value: summaryResponse?.jsCompletedJobs
                            ? `${summaryResponse?.jsCompletedJobs} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "On Time Placement",
                        value: summaryResponse?.jsOnTimePlacement
                            ? `${summaryResponse?.jsOnTimePlacement} %`
                            : "0 %",
                    },
                    {
                        title: "On Time Delivery",
                        value: summaryResponse?.jsOnTimeDelivery
                            ? `${summaryResponse?.jsOnTimeDelivery} %`
                            : "0 %",
                    },
                    {
                        title: "Late Booking",
                        value: summaryResponse?.jsLateBooking
                            ? `${summaryResponse?.jsLateBooking} %`
                            : "0 %",
                    },
                    {
                        title: "Same Day Delivery",
                        value: summaryResponse?.jsSameDayDelivery
                            ? `${summaryResponse?.jsSameDayDelivery} %`
                            : "0 %",
                    },
                    {
                        title: "Transit Time",
                        value: summaryResponse?.jsTransitTime ?? "0h",
                    },
                    {
                        title: "Spent At Pickup",
                        value: summaryResponse?.jsSpentAtPickup ?? "0h",
                    },
                    {
                        title: "Spent At Pickup Average",
                        value: summaryResponse?.jsSpentAtPickupAvg ?? "0h",
                    },
                    {
                        title: "Spent At Pickup More Than 5 hours",
                        value: summaryResponse?.jsSpentAtPickupMtFiveh
                            ? `${summaryResponse?.jsSpentAtPickupMtFiveh} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "On Time Booking",
                        value: summaryResponse?.jsOnTimeBooking
                            ? `${summaryResponse?.jsOnTimeBooking} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Confirmed Jobs",
                        value: summaryResponse?.jsConfirmedJobs
                            ? `${summaryResponse?.jsConfirmedJobs} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Disputed Jobs",
                        value: summaryResponse?.jsDisputedJobs
                            ? `${summaryResponse?.jsDisputedJobs} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Resolved Jobs",
                        value: summaryResponse?.jsResolvedJobs
                            ? `${summaryResponse?.jsResolvedJobs} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Spent At Delivery",
                        value: summaryResponse?.jsSpentAtDelivery ?? "0h",
                    },
                    {
                        title: "Spent At Delivery Average",
                        value: summaryResponse?.jsSpentAtDeliveryAvg ?? "0h",
                    },
                    {
                        title: "Spent At Delivery More Than 5 hours",
                        value: summaryResponse?.jsSpentAtDeliveryMtFiveh
                            ? `${summaryResponse?.jsSpentAtDeliveryMtFiveh} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "On Time Placement For On Time Booking",
                        value: summaryResponse?.jsOtPlForOtBooking
                            ? `${summaryResponse?.jsOtPlForOtBooking} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Finance Confirmed Jobs",
                        value: summaryResponse?.jsFinComJobs
                            ? `${summaryResponse?.jsFinComJobs} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Finance Disputed Jobs",
                        value: summaryResponse?.jsFinDisJob
                            ? `${summaryResponse?.jsFinDisJob} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Finance Resolved Jobs",
                        value: summaryResponse?.jsFinResJob
                            ? `${summaryResponse?.jsFinResJob} Jobs`
                            : "0 Jobs",
                    },
                    {
                        title: "Total Transport Charge",
                        value: summaryResponse?.jsTransportCh
                            ? formatter.format(summaryResponse?.jsTransportCh)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Total Demurrage Charge",
                        value: summaryResponse?.jsDemCh
                            ? formatter.format(summaryResponse?.jsDemCh)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Total Night Allowance Charge",
                        value: summaryResponse?.jsNiAllCh
                            ? formatter.format(summaryResponse?.jsNiAllCh)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Total Management Charge",
                        value: summaryResponse?.jsManagementCh
                            ? formatter.format(summaryResponse?.jsManagementCh)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Total Other Charge",
                        value: summaryResponse?.jsOtherCh
                            ? formatter.format(summaryResponse?.jsOtherCh)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Total Invoice Amount",
                        value: summaryResponse?.jsTotInvAmt
                            ? formatter.format(summaryResponse?.jsTotInvAmt)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Total Credit Debit Amount",
                        value: summaryResponse?.jsCrDebAmt
                            ? formatter.format(summaryResponse?.jsCrDebAmt)
                            : formatter.format(0.0),
                    },
                    {
                        title: "Final Amount",
                        value: summaryResponse?.jsTotCh
                            ? formatter.format(summaryResponse?.jsTotCh)
                            : formatter.format(0.0),
                    },
                ];

                setSummaryData(formattedSummary);
            }
        } catch (error) {
            toast.error("Error fetching data: " + error.message, {
                autoClose: 5000,
                containerId: "Main",
            });
        } finally {
            setSearchResultsPending(false);
        }
    };

    const handleAccordionClick = (index) => {
        setActiveAccordion(index === activeAccordion ? null : index);
    };

    const handleReset = () => {
        setSearchResults([]);
        setSearchClicked(false);
        setSummaryData(null);
    };

    return (
        <div>
            {/* Accordion for Search */}
            <div
                className="ui styled accordion"
                style={{ width: "100%", marginBottom: "10px" }}
            >
                <div
                    className={`title ${activeAccordion === 1 ? "active" : ""}`}
                    onClick={() => handleAccordionClick(1)}
                >
                    <div>
                        Search
                        <Search size={14} style={{ marginLeft: "10px" }} />
                        <i className="dropdown icon"></i>
                    </div>
                </div>
                <div className={`content ${activeAccordion === 1 ? "active" : ""}`}>
                    <PerformanceFinanceSearch
                        searchResHandler={handleSearch}
                        resetHandler={handleReset}
                        dateType={dateType}
                        setDateType={setDateType}
                    />
                    <div className="ui clearing divider"></div>
                </div>
            </div>

            {/* Grid Section */}
            {isSearchClicked && (
                <>
                    {isSearchResultsPending || isHeaderLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <Grid
                            heading={reportName}
                            showDownloadButton={true}
                            downloadParam={""}
                            downloadAction="Performance Finance Report Download"
                            downloadOnlySelected={false}
                            excelSheetDownload={true}
                            config={config}
                            content={searchResults}
                            ref={gridRef}
                            searchable={true}
                            handleDownloadPopUpChange={() => {
                                const customFileName = `${reportName.replace(/ /g, "_")}_${new Date().toISOString().split("T")[0]}.csv`;
                                gridRef.current.download({ fileName: customFileName });
                            }}
                        />
                    )}
                </>
            )}
            {summaryData && (
                <InfoList
                    content={summaryData}
                    title="Performance Finance Summary"
                />
            )}
        </div>
    );
}

export default PerformanceFinanceScreen;
